import React from 'react';
import { Shield, ArrowLeft, CheckCircle, Scale } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Link } from 'react-router-dom';

const Privacy = () => {
  const sections = [
    {
      title: '1. Introduction',
      content: `Rocket Van Express (Pty) Ltd ("we", "us", "our") is committed to protecting your personal information and your right to privacy in accordance with South African law.

This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our delivery and mini-move services. It is drafted in compliance with:
- The Protection of Personal Information Act, 2013 (Act No. 4 of 2013) ("POPIA")
- The Promotion of Access to Information Act, 2000 (Act No. 2 of 2000) ("PAIA")
- The Electronic Communications and Transactions Act, 2002 (Act No. 25 of 2002) ("ECTA")

By using our services, you acknowledge that you have read, understood, and consent to the data practices described in this policy. You may withdraw your consent at any time by contacting our Information Officer.`
    },
    {
      title: '2. Responsible Party',
      content: `In terms of POPIA, the Responsible Party for the processing of your personal information is:

Rocket Van Express (Pty) Ltd
Western Cape, South Africa
Email: Info@rocketvan.co.za
Phone: +27 736596239`
    },
    {
      title: '3. Information We Collect',
      content: `We collect and process only the minimum personal information necessary to provide our services (data minimisation principle under POPIA Section 10).

Personal Information You Provide:
- Full name and contact details (phone number, email address, WhatsApp number)
- Pickup and delivery addresses
- Payment information (billing details, banking information)
- Business information (for business accounts: company name, registration details, tax number)
- Communication records (WhatsApp messages, emails, phone calls)
- Photos of items for quote purposes (if provided)
- Proof-of-delivery photos and signatures

Information Automatically Collected:
- Website usage data (IP address, browser type, device information)
- Booking timestamps and service history
- Location data (for route optimisation and driver dispatch)
- Transaction history and payment records
- Cookie data (see our Cookie Policy for details)

Special Personal Information:
We do not intentionally collect any special personal information as defined in Section 26 of POPIA (e.g., religious beliefs, race, trade union membership, health information, biometric data). Should we inadvertently receive such information, it will be deleted immediately.`
    },
    {
      title: '4. Purpose of Processing (POPIA Section 13)',
      content: `We use your personal information strictly for the following purposes:
- Process and fulfill your delivery and mini-move bookings
- Communicate booking confirmations, updates, and delivery notifications via WhatsApp
- Generate quotes and invoices
- Process payments and maintain billing records
- Provide customer support and respond to inquiries
- Send proof-of-delivery confirmations
- Improve our services through anonymised data analysis
- Comply with legal obligations and resolve disputes
- Send promotional offers and service updates (only with your explicit opt-in consent)
- Maintain business account relationships and consolidated reporting
- Detect and prevent fraud or unlawful activity

We will not process your personal information for any purpose incompatible with the above unless we obtain your further consent or are required to do so by law.`
    },
    {
      title: '5. Legal Basis for Processing (POPIA Section 11)',
      content: `We process your personal information based on one or more of the following lawful grounds:
- Consent (Section 11(1)(a)): You have given explicit, informed, and voluntary consent for specific purposes
- Contractual necessity (Section 11(1)(b)): Processing is necessary to perform our service agreement with you
- Legal obligation (Section 11(1)(c)): We are required by law to process certain information (e.g., tax legislation, consumer protection laws)
- Legitimate interests (Section 11(1)(f)): Processing is necessary for our legitimate business operations, provided it does not prejudice your rights and freedoms

Processing Limitation:
In accordance with POPIA Section 10, we only process personal information that is adequate, relevant, and not excessive for the stated purposes.`
    },
    {
      title: '6. Information Sharing and Disclosure',
      content: `We may share your information with the following categories of recipients, each bound by confidentiality obligations:

Service Delivery:
- Our drivers and dispatch team (name, contact details, addresses — limited to what is necessary)
- Payment processors (for transaction processing only)

Business Operations:
- Accounting and invoicing services
- IT service providers and hosting platforms
- Customer support tools

Legal Requirements:
- Law enforcement or government agencies when required by law or court order
- The Information Regulator, if requested
- Legal proceedings or to protect our rights and safety

We do NOT:
- Sell your personal information to third parties
- Share your data for third-party marketing purposes without your consent
- Transfer data outside the Republic of South Africa without ensuring adequate safeguards as required by POPIA Section 72 (including binding corporate rules, contractual obligations, or the recipient being subject to equivalent privacy laws)`
    },
    {
      title: '7. Data Security (POPIA Section 19)',
      content: `We implement appropriate technical and organisational security measures to protect your personal information against loss, unauthorised access, alteration, or destruction:
- Encrypted data transmission (HTTPS/TLS)
- Secure payment processing (PCI-compliant providers)
- Access controls and authentication (role-based access)
- Regular security assessments and vulnerability testing
- Staff training on data protection and POPIA obligations
- Incident response procedures

In the event of a data breach that poses a risk to you, we will notify the Information Regulator and affected data subjects as soon as reasonably possible, in accordance with POPIA Section 22.

No method of transmission over the internet is 100% secure. While we take every reasonable step to protect your information, we cannot guarantee absolute security.`
    },
    {
      title: '8. Data Retention (POPIA Section 14)',
      content: `We retain your personal information only for as long as is necessary to fulfill the purposes for which it was collected, or as required by law.

Retention periods:
- Active customer data: Duration of the relationship + 1 year
- Transaction records: 5 years (as required by the Tax Administration Act and Companies Act)
- Communication records: 2 years
- Marketing consent records: Until you withdraw consent + 30 days
- Proof-of-delivery records: 3 years

After the applicable retention period expires, personal information is securely deleted or de-identified so that it can no longer be associated with you. Destruction of records is carried out in accordance with POPIA Section 14(4).`
    },
    {
      title: '9. Your Rights as a Data Subject (POPIA Sections 23–25)',
      content: `Under POPIA, you have the following rights regarding your personal information:

- Right to be informed (Section 18): Know what personal information we hold and how we use it
- Right of access (Section 23): Request a copy of your personal information we hold
- Right to correction (Section 24): Request that inaccurate, irrelevant, excessive, out-of-date, incomplete, or misleading information be corrected or deleted
- Right to deletion (Section 24): Request deletion of your personal information where it is no longer necessary (subject to legal retention requirements)
- Right to object (Section 11(3)(a)): Object to the processing of your personal information for direct marketing purposes
- Right to restrict processing: Request that we limit how we use your data in certain circumstances
- Right to withdraw consent: Withdraw your consent at any time. Withdrawal does not affect the lawfulness of processing that occurred before withdrawal
- Right to submit a complaint: Lodge a complaint with the Information Regulator if you believe your rights have been infringed

To exercise any of these rights, contact our Information Officer:
Email: privacy@rocketvan.co.za
Phone: +27 736596239
WhatsApp: +27 736596239

We will acknowledge your request within 5 business days and respond substantively within 30 days, as required by POPIA. We may request proof of identity before processing your request to protect your information from unauthorised access.`
    },
    {
      title: '10. Direct Marketing (POPIA Section 69)',
      content: `In accordance with POPIA Section 69 and the Consumer Protection Act:

With your explicit opt-in consent, we may send:
- Promotional offers and special deals
- Service updates and new features
- Tips and helpful content
- Business account updates and reports

You may opt out at any time by:
- Replying "STOP" to marketing WhatsApp messages
- Clicking "unsubscribe" in marketing emails
- Contacting us directly to update your preferences

Once you opt out, we will cease direct marketing communications within 5 business days.

Note: You cannot opt out of essential transactional communications (booking confirmations, delivery updates, invoices) as these are necessary for service delivery.`
    },
    {
      title: '11. Cookies and Tracking Technologies',
      content: `Our website uses cookies and similar technologies. For full details on the types of cookies we use, how to manage them, and your choices, please refer to our separate Cookie Policy.

In summary:
- Essential cookies (required for website operation) — cannot be disabled
- Analytics cookies (Google Analytics — anonymised data) — optional
- Marketing cookies (advertising and remarketing) — optional
- Preference cookies (remember your settings) — optional

You can manage your cookie preferences at any time through the cookie settings on our website or through your browser settings.`
    },
    {
      title: '12. Third-Party Links',
      content: `Our website may contain links to third-party websites and services (e.g., Google Maps, payment gateways, social media platforms). We are not responsible for the privacy practices of these external sites. We encourage you to review their respective privacy policies before providing any personal information.`
    },
    {
      title: '13. Children\'s Privacy',
      content: `Our services are not directed to individuals under 18 years of age. We do not knowingly collect personal information from children as defined in POPIA. If we become aware that we have collected data from a child without the consent of a competent person (parent or guardian), we will take steps to delete it immediately in accordance with POPIA Section 35.`
    },
    {
      title: '14. Automated Decision-Making',
      content: `We do not use automated decision-making or profiling that produces legal effects or similarly significant effects on you without human intervention. Any automated processes (such as route optimisation or pricing estimates) are used solely as aids to our team and do not constitute binding decisions.`
    },
    {
      title: '15. Changes to This Policy',
      content: `We may update this Privacy Policy from time to time to reflect:
- Changes in our data processing practices
- Legal or regulatory requirements
- New service features or business operations

Changes will be posted on this page with an updated "Last Updated" date. Material changes will be communicated directly to active customers via email or WhatsApp at least 14 days before they take effect.

Continued use of our services after the effective date of changes constitutes acceptance of the updated policy. If you do not agree with the changes, you may discontinue use of our services and request deletion of your data.`
    },
    {
      title: '16. Information Officer (POPIA Section 55)',
      content: `In terms of POPIA, our designated Information Officer is responsible for ensuring compliance with data protection legislation and handling all data subject requests.

Information Officer Contact Details:
Email: privacy@rocketvan.co.za
General Enquiries: Info@rocketvan.co.za
Phone: +27 736596239

Contact the Information Officer for:
- POPIA and PAIA-related enquiries
- Data subject access, correction, or deletion requests
- Privacy concerns or complaints
- Reporting a suspected data breach

PAIA Manual:
A copy of our PAIA manual (as required by Section 51 of PAIA) is available upon request by emailing privacy@rocketvan.co.za.`
    },
    {
      title: '17. Complaints and the Information Regulator',
      content: `If you are unsatisfied with our response to a data protection concern, or believe your privacy rights have been violated, you may:

1. Contact us directly:
   Email: privacy@rocketvan.co.za
   WhatsApp: +27 736596239
   We will investigate and provide a substantive response within 30 days.

2. Lodge a complaint with the Information Regulator (South Africa):
   Physical address: JD House, 27 Stiemens Street, Braamfontein, Johannesburg, 2001
   Postal address: P.O. Box 31533, Braamfontein, Johannesburg, 2017
   Complaints email: complaints.IR@justice.gov.za
   General enquiries: inforeg@justice.gov.za
   Phone: +27 (0)10 023 5207
   Website: https://inforegulator.org.za`
    }
  ];

  const dataCollectionSummary = [
    { category: 'Contact Details', examples: 'Name, phone, email, address' },
    { category: 'Service Data', examples: 'Booking details, delivery addresses, photos' },
    { category: 'Payment Info', examples: 'Billing details, transaction history' },
    { category: 'Communications', examples: 'WhatsApp messages, emails, calls' }
  ];

  return (
    <div className="min-h-screen bg-gray-50" data-testid="privacy-page">
      {/* Hero */}
      <section className="bg-gradient-to-br from-[#001A47] via-[#002B6B] to-[#001A47] text-white py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <Link to="/" className="inline-flex items-center text-gray-300 hover:text-white mb-6 transition-colors">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Home
            </Link>
            <Shield className="h-12 w-12 text-[#039fff] mb-4" />
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-gray-300">POPIA & PAIA Compliant | Last updated: February 15, 2026</p>
          </div>
        </div>
      </section>

      {/* Compliance Banner */}
      <section className="py-6 bg-[#001A47]/5 border-b">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto flex items-start gap-4 p-4 bg-white rounded-lg border border-[#039fff]/20">
            <Scale className="h-6 w-6 text-[#039fff] flex-shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-[#001A47] text-sm">South African Data Protection Compliance</p>
              <p className="text-xs text-gray-600 mt-1">
                This policy complies with the Protection of Personal Information Act (POPIA), Act 4 of 2013, 
                and the Promotion of Access to Information Act (PAIA), Act 2 of 2000. Our Information Officer 
                can be reached at <a href="mailto:privacy@rocketvan.co.za" className="text-[#039fff] hover:underline">privacy@rocketvan.co.za</a>.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Quick Summary */}
      <section className="py-8 bg-[#039fff]/5">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-lg font-bold text-[#001A47] mb-4">Privacy at a Glance</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {dataCollectionSummary.map((item, index) => (
                <div key={index} className="flex items-start space-x-3 bg-white p-4 rounded-lg">
                  <CheckCircle className="h-5 w-5 text-[#039fff] flex-shrink-0 mt-0.5" />
                  <div>
                    <p className="font-semibold text-[#001A47]">{item.category}</p>
                    <p className="text-sm text-gray-600">{item.examples}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Content */}
      <section className="py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto bg-white rounded-lg shadow-sm p-8 md:p-12">
            <div className="prose prose-lg max-w-none">
              <div className="space-y-8">
                {sections.map((section, index) => (
                  <div key={index} id={`section-${index + 1}`} className="scroll-mt-20">
                    <h2 className="text-lg font-bold text-[#001A47] mb-4">{section.title}</h2>
                    <div className="text-gray-700 whitespace-pre-line leading-relaxed text-sm">
                      {section.content}
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-12 p-6 bg-[#fb6402]/5 rounded-lg border-l-4 border-[#fb6402]">
                <h3 className="font-bold text-[#001A47] mb-2">Your Privacy Matters</h3>
                <p className="text-gray-700 mb-4 text-sm">
                  We are committed to protecting your personal information and fully complying with POPIA and PAIA. 
                  If you have any questions or concerns about how we handle your data, please contact our Information Officer.
                </p>
                <p className="text-sm text-gray-600">
                  Information Officer: <a href="mailto:privacy@rocketvan.co.za" className="text-[#039fff] hover:underline">privacy@rocketvan.co.za</a><br />
                  General: <a href="mailto:Info@rocketvan.co.za" className="text-[#039fff] hover:underline">Info@rocketvan.co.za</a><br />
                  WhatsApp: <a href="https://wa.me/27736596239" className="text-[#039fff] hover:underline">+27 736596239</a>
                </p>
              </div>

              <div className="mt-8 flex flex-wrap gap-4">
                <Link to="/terms">
                  <Button variant="outline">Terms & Conditions</Button>
                </Link>
                <Link to="/cookies">
                  <Button variant="outline">Cookie Policy</Button>
                </Link>
                <Link to="/contact">
                  <Button variant="outline">Contact Us</Button>
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Privacy;
